import { useEffect, useState } from 'react';

import {
  getSpeciesRegulationsAtLocation,
  Regulations,
} from '../../../api/fishrules/getSpeciesRegulationsAtLocation';
import { ICoordinates } from '@app-types/geo';
import { reportMykissClientError } from '@lib/errors/client';
import { useHandleUnmount } from '@hooks/useHandleUnmount';

interface Result {
  isLoading: boolean;
  regulation?: Regulations;
  fetchedAt?: string;
}

type Params = ICoordinates & { fishrulesSpeciesId: number };

export function useSpeciesRegulationAtLocation(params: Params | undefined): Result {
  const [isLoading, setIsLoading] = useState(false);
  const [regulation, setRegulation] = useState<Regulations>();
  const [fetchedAt, setFetchedAt] = useState<string>();

  const latitude = params?.latitude;
  const longitude = params?.longitude;
  const fishrulesSpeciesId = params?.fishrulesSpeciesId;
  const unmounted = useHandleUnmount();

  useEffect(() => {
    const controller = new AbortController();

    if (fishrulesSpeciesId !== undefined && latitude !== undefined && longitude !== undefined) {
      setIsLoading(true);
      getSpeciesRegulationsAtLocation({
        latitude,
        longitude,
        species: [{ fishrulesSpeciesIds: [fishrulesSpeciesId] }],
        signal: controller.signal,
      })
        .then(results => {
          if (unmounted.current) {
            return;
          }
          const firstResult = results[0];
          if (firstResult?.regulations) {
            setRegulation(firstResult.regulations);
            setFetchedAt(firstResult.fetchedAt);
          } else {
            setRegulation(undefined);
            setFetchedAt(undefined);
          }
          setIsLoading(false);
        })
        .catch((error: unknown) => {
          if (error instanceof Error && error.name !== 'AbortError') {
            reportMykissClientError(error);
          }
          if (!unmounted.current) {
            setIsLoading(false);
          }
        });
    }

    return () => {
      controller.abort();
    };
  }, [latitude, longitude, fishrulesSpeciesId, unmounted]);

  return { isLoading, regulation, fetchedAt };
}
